import dayjs from "dayjs";

type ComponentAdapter = {
  adaptProps: (
    props: Record<string, unknown>,
    context: {
      runRequestEvent: (
        requestEvent: string,
        payload: Record<string, unknown>,
      ) => Promise<unknown>;
    },
  ) => Record<string, unknown>;
};

type InitialValuePreset =
  | "currentMonth"
  | "lastMonth"
  | "last7Days"
  | "last30Days"
  | "currentWeek";

function getValueByPath(source: unknown, path: string): unknown {
  const finalPath = String(path || "").trim();
  if (!finalPath) return undefined;
  return finalPath.split(".").reduce<unknown>((acc, segment) => {
    if (!segment) return acc;
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[segment];
    }
    return undefined;
  }, source);
}

function normalizeSelectOptionsFromResult(
  result: unknown,
): Array<{ label: string; value: string | number }> {
  const source = getValueByPath(result, "data");
  const list = Array.isArray(source)
    ? source
    : Array.isArray(result)
      ? result
      : [];
  return list.reduce<Array<{ label: string; value: string | number }>>(
    (acc, item) => {
      const record =
        item && typeof item === "object"
          ? (item as Record<string, unknown>)
          : {};
      const value = record.value ?? record.id ?? record.code;
      if (value === undefined || value === null) return acc;
      const text = record.label ?? record.name ?? record.text ?? value;
      acc.push({
        label: String(text),
        value: value as string | number,
      });
      return acc;
    },
    [],
  );
}

function resolveColumnRequestEvent(column: Record<string, unknown>): string {
  const direct = String(column._requestEvent || "").trim();
  if (direct) return direct;
  return "";
}

function buildColumnRequestPayload(
  requestEvent: string,
  params: Record<string, unknown>,
  column: Record<string, unknown>,
): Record<string, unknown> {
  const requestParams =
    params && typeof params === "object" && !Array.isArray(params)
      ? params
      : {};
  const field = String(column.dataIndex || column.key || "").trim();
  return {
    ...requestParams,
    requestEvent,
    params: requestParams,
    field,
    column,
  };
}

function resolveDateRangePresetInitialValue(
  preset: InitialValuePreset,
): [unknown, unknown] | undefined {
  const now = dayjs();
  switch (preset) {
    case "currentMonth":
      return [now.startOf("month"), now.endOf("month")];
    case "lastMonth": {
      const base = now.subtract(1, "month");
      return [base.startOf("month"), base.endOf("month")];
    }
    case "last7Days":
      return [now.subtract(6, "day").startOf("day"), now.endOf("day")];
    case "last30Days":
      return [now.subtract(1, "month").startOf("day"), now.endOf("day")];
    case "currentWeek":
      return [now.startOf("week"), now.endOf("week")];
    default:
      return undefined;
  }
}

function normalizeColumnFormItemProps(column: Record<string, unknown>) {
  const valueType = String(column.valueType || "");
  if (!["dateRange", "dateTimeRange"].includes(valueType)) {
    return column.formItemProps;
  }
  const raw =
    column.formItemProps &&
    typeof column.formItemProps === "object" &&
    !Array.isArray(column.formItemProps)
      ? (column.formItemProps as Record<string, unknown>)
      : undefined;
  if (!raw) return column.formItemProps;
  if (String(raw.initialValueMode || "") !== "preset") return raw;
  const preset = String(raw.initialValuePreset || "") as InitialValuePreset;
  if (!preset) return raw;
  const rangeValue = resolveDateRangePresetInitialValue(preset);
  if (!rangeValue) return raw;
  return {
    ...raw,
    initialValue: rangeValue,
  };
}

function attachColumnsRequestByEvent(
  columns: unknown,
  runRequestEvent: (
    requestEvent: string,
    payload: Record<string, unknown>,
  ) => Promise<unknown>,
): unknown {
  if (!Array.isArray(columns)) return columns;
  return columns.map((column) => {
    if (!column || typeof column !== "object") return column;
    const current = column as Record<string, unknown>;
    const normalizedFormItemProps = normalizeColumnFormItemProps(current);
    const normalizedColumn =
      normalizedFormItemProps === current.formItemProps
        ? current
        : {
            ...current,
            formItemProps: normalizedFormItemProps,
          };
    const requestEvent = resolveColumnRequestEvent(current);
    if (!requestEvent) return normalizedColumn;
    return {
      ...normalizedColumn,
      request: async (params: Record<string, unknown>) => {
        const payload = buildColumnRequestPayload(
          requestEvent,
          params,
          current,
        );
        const result = await runRequestEvent(requestEvent, payload);
        return normalizeSelectOptionsFromResult(result);
      },
    };
  });
}

export const columnsRequestAdapter: ComponentAdapter = {
  adaptProps: (props, context) => {
    if (!("columns" in props)) return props;
    return {
      ...props,
      columns: attachColumnsRequestByEvent(props.columns, context.runRequestEvent),
    };
  },
};
